import {
  Box,
  HStack,
  KdsBadge,
  KdsButton,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiArrowRightLine, RiFlashlightFill } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import dayjs from "dayjs";
import { OrderItemLogActionTypeEnum } from "@/config/apiClient.types";
import { getIsSelectedDateInProductionTime } from "@/helpers/dates/getIsSelectedDateIsInProductionTime";
import { useOrders, useToasts } from "@/hooks";
import { type TransitionData } from "@/types/transitionData";
import { type RushOrderBlocProps } from "./RushOrderBloc.types";

export const RushOrderBloc = ({
  orderItem,
  setIsCancelModalOpen,
}: RushOrderBlocProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();

  const { useRushOrder } = useOrders();
  const { mutateAsync: handleRushOrder } = useRushOrder();

  const orderedLogsByCreationDateDesc = orderItem?.OrderLog.filter(
    (log) =>
      log.actionType === OrderItemLogActionTypeEnum.PLACED ||
      log.actionType === OrderItemLogActionTypeEnum.RUSH_ORDER_ACCEPTED_DATE ||
      log.actionType === OrderItemLogActionTypeEnum.RUSH_ORDER_NEW_DATE_PROPOSAL
  ).sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const isRushOrder =
    orderedLogsByCreationDateDesc?.[0]?.actionType ===
      OrderItemLogActionTypeEnum.RUSH_ORDER_NEW_DATE_PROPOSAL &&
    orderItem?.sandboxItem.deliveryDate &&
    orderItem?.sandboxItem.productionTime &&
    getIsSelectedDateInProductionTime(
      new Date(orderItem.sandboxItem.deliveryDate),
      orderItem.sandboxItem.productionTime
    );

  const displayRushOrderUI = isRushOrder && orderItem?.status !== "CANCELLED";

  const transitionData = orderedLogsByCreationDateDesc?.[0]
    ?.transitionData as TransitionData;
  const rawDate = transitionData?.newDeliveryDate;

  const validateRushOrderDeliveryDate = async (newDeliveryDate: string) => {
    if (!newDeliveryDate || !orderItem?.id || !orderItem.order?.id) {
      return;
    }

    try {
      await handleRushOrder({
        newDeliveryDate,
        isValidatedByClient: true,
        orderId: orderItem.order.id,
        orderItemId: orderItem.id,
      });

      add({
        title: t("rushOrderProposal.toast.success.contentNoDate"),
        state: "success",
      });
    } catch (error) {
      console.error("Error validating rush order delivery date:", error);

      add({
        title: t("rushOrderProposal.toast.error.content"),
        state: "error",
      });
    }
  };

  if (!displayRushOrderUI) {
    return null;
  }

  return (
    <Box rounded="br-md" bgColor="elevation.first" p="sp-lg">
      <VStack
        align="flex-start"
        justify="center"
        wrap={{ base: "wrap", md: "nowrap" }}
        gap="sp-xl"
      >
        <KdsText size="subtitle-sm">
          {t("order.myOrdersItemDetails.rushOrder.title")}
        </KdsText>

        <Box
          rounded="br-md"
          borderColor="surface.disabled"
          borderWidth="bw-small"
          bgColor="elevation.first"
          p="sp-lg"
        >
          <VStack align="flex-start" justify="center" gap="sp-md">
            {/* <div className="flex w-full flex-col items-start justify-center gap-4 rounded-lg border border-solid border-slate-200 p-6"> */}
            <HStack gap="sp-md">
              <KdsText size="subtitle-sm">
                {t("order.myOrdersItemDetails.rushOrder.subtitle")}
              </KdsText>

              <KdsBadge iconLeft={RiFlashlightFill} size="sm">
                {t(
                  "order.myOrdersItemDetails.rushOrder.badgeLabelRequiredAction"
                )}
              </KdsBadge>
            </HStack>

            <KdsText color="text-bodyvariant">
              {t("order.myOrdersItemDetails.rushOrder.content")}
            </KdsText>

            <HStack align="center" gap="sp-xs">
              <KdsText>
                {t("order.myOrdersItemDetails.rushOrder.deliveryDate")}
              </KdsText>

              <KdsText decoration="line-through">
                {dayjs(orderItem?.sandboxItem.deliveryDate).format("L")}
              </KdsText>

              <RiArrowRightLine className="size-sp-md" />
              <KdsText>{rawDate ? dayjs(rawDate).format("L") : ""}</KdsText>
            </HStack>
            {/* </div> */}
          </VStack>
        </Box>

        <HStack justify="flex-end" gap="sp-xs" w="100%">
          <KdsButton
            variant="outlined"
            color="error"
            onPress={() => setIsCancelModalOpen(true)}
          >
            {t("order.myOrdersItemDetails.rushOrder.buttonLabelCancel")}
          </KdsButton>

          <KdsButton
            onPress={() => {
              if (rawDate) {
                void validateRushOrderDeliveryDate(rawDate);
              }
            }}
          >
            {t("buttons.accept")}
          </KdsButton>
        </HStack>
      </VStack>
    </Box>
  );
};
